import { Box, Card, CardContent, Typography } from '@mui/material'
import React, { useState } from 'react'

import UnscheduledProjectsDialog from './UnscheduledProjectsDialog'

interface UnscheduledProjectsCardProps {
  unscheduledCount: number
  isLoading: boolean
  error: Error | null
}

const UnscheduledProjectsCard: React.FC<UnscheduledProjectsCardProps> = ({
  unscheduledCount,
  isLoading,
  error,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleCardClick = () => {
    if (!isLoading && !error && unscheduledCount > 0) {
      setDialogOpen(true)
    }
  }

  const shimmerKeyframes = `
    @keyframes shimmer {
      0% { 
        transform: translateX(-100%);
      }
      100% { 
        transform: translateX(100%);
      }
    }
  `

  const numberContainerStyle = {
    height: '2rem',
    mb: 2,
    display: 'flex',
    alignItems: 'center',
  }

  const skeletonStyle = {
    display: 'inline-block',
    width: '48px',
    height: '2rem',
    backgroundColor: '#e0e0e0',
    borderRadius: '8px',
    position: 'relative' as const,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background:
        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
      animation: 'shimmer 1.5s infinite',
    },
  }

  if (error) {
    return (
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1 }}>
          <Typography color="error">Failed to load data</Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <style>{shimmerKeyframes}</style>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: isLoading || error || unscheduledCount === 0 ? 'default' : 'pointer',
          '&:hover': {
            ...(!isLoading &&
              !error &&
              unscheduledCount > 0 && {
                boxShadow: 3,
                transform: 'translateY(-1px)',
              }),
          },
          transition: 'all 0.2s ease-in-out',
        }}
        onClick={handleCardClick}
      >
        <CardContent sx={{ p: 3, flexGrow: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2,
              fontSize: '1rem',
            }}
          >
            Unscheduled Projects ({'>'}24hr)
          </Typography>
          <Box sx={numberContainerStyle}>
            {isLoading ? (
              <Box sx={skeletonStyle} />
            ) : (
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: '#F44336',
                  fontSize: '2rem',
                  lineHeight: 1,
                }}
              >
                {unscheduledCount}
              </Typography>
            )}
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'text.primary',
              fontSize: '1rem',
              fontWeight: 500,
            }}
          >
            Projects requiring attention
          </Typography>
        </CardContent>
      </Card>
      <UnscheduledProjectsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        unscheduledCount={unscheduledCount}
      />
    </>
  )
}

export default UnscheduledProjectsCard
